import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading recent searches:', e);
      }
    }
  }, []);

  // Open spotlight
  const openSpotlight = useCallback(() => {
    setIsSpotlightOpen(true);
    setSearchQuery('');
  }, []);

  // Close spotlight
  const closeSpotlight = useCallback(() => {
    setIsSpotlightOpen(false);
    setSearchQuery('');
  }, []);

  // Toggle spotlight
  const toggleSpotlight = useCallback(() => {
    setIsSpotlightOpen(prev => !prev);
    if (isSpotlightOpen) {
      setSearchQuery('');
    }
  }, [isSpotlightOpen]);

  // Add to recent searches
  const addToRecentSearches = useCallback((query) => {
    if (!query || query.trim() === '') return;
    
    setRecentSearches(prev => {
      // Remove duplicate if exists and add to beginning
      const filtered = prev.filter(s => s.toLowerCase() !== query.toLowerCase());
      const updated = [query, ...filtered].slice(0, 5); // Keep only 5 recent searches
      
      // Save to localStorage
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      
      return updated;
    });
  }, []);

  // Clear recent searches
  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  }, []);

  // Global keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K to open spotlight
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleSpotlight();
      }
      
      // Escape to close spotlight
      if (e.key === 'Escape' && isSpotlightOpen) {
        closeSpotlight();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSpotlightOpen, toggleSpotlight, closeSpotlight]);

  const value = {
    isSpotlightOpen,
    openSpotlight,
    closeSpotlight,
    toggleSpotlight,
    searchQuery,
    setSearchQuery,
    recentSearches,
    addToRecentSearches,
    clearRecentSearches
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};